import {
  GetTimeSettingsCustomTimeRangeCategoriesDocument,
  TimeSettingsCustomTimeRangeCategory,
  TimeSettingsEnumsCategoryTypeEnum,
  TimeSettingsEnumsTimeTypeEnum,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'

export const useFetchTimeSettingsCustomTimeRangeCategories = (
  companyId: number,
  active?: boolean
) => {
  const { data: categories, loading } = useQuery(GetTimeSettingsCustomTimeRangeCategoriesDocument, {
    variables: { companyId, active },
    fetchPolicy: 'cache-first',
  })

  const categoriesCollection = new CustomTimeRangeCategoriesCollection(
    categories?.timeSettings.customTimeRangeCategoriesConnection.nodes || []
  )
  const hasTimeSettingsCategories = categoriesCollection.customTimeRangeCategories.length > 0

  const archivedCategories = categoriesCollection.customTimeRangeCategories.filter(
    (category) => category.archived
  )

  return {
    loading,
    categories: categoriesCollection,
    hasTimeSettingsCategories,
    archivedCategories,
  }
}

export class CustomTimeRangeCategoriesCollection {
  customTimeRangeCategories: CustomTimeRangeCategory[]

  constructor(customTimeRangeCategories: TimeSettingsCustomTimeRangeCategory[]) {
    this.customTimeRangeCategories = customTimeRangeCategories.map(
      (category) => new CustomTimeRangeCategory(category)
    )
  }

  get activeCategories(): CustomTimeRangeCategory[] {
    return this.customTimeRangeCategories.filter((category) => category.isActive)
  }

  get regularActiveCategories(): CustomTimeRangeCategory[] {
    return this.customTimeRangeCategories.filter(
      (category) => category.isActive && category.isRegular
    )
  }

  get overtimeCategories(): CustomTimeRangeCategory[] {
    return this.customTimeRangeCategories.filter((category) => category.isOvertime)
  }

  get anyTimeCategories(): CustomTimeRangeCategory[] {
    return this.customTimeRangeCategories.filter((category) => category.isAnyTime)
  }

  get overtimeAndAnyTimeCategories(): CustomTimeRangeCategory[] {
    return this.customTimeRangeCategories.filter(
      (category) => category.isOvertime || category.isAnyTime
    )
  }

  get regularAndAnyTimeCategories(): CustomTimeRangeCategory[] {
    return this.customTimeRangeCategories.filter(
      (category) => category.isRegular || category.isAnyTime
    )
  }

  get regularCategories(): CustomTimeRangeCategory[] {
    return this.customTimeRangeCategories.filter((category) => category.isRegular)
  }

  get overtimeActiveCategories(): CustomTimeRangeCategory[] {
    return this.customTimeRangeCategories.filter(
      (category) => category.isActive && category.isOvertime
    )
  }

  get anyTimeActiveCategories(): CustomTimeRangeCategory[] {
    return this.customTimeRangeCategories.filter(
      (category) => category.isActive && category.isAnyTime
    )
  }

  get archivedCategories(): CustomTimeRangeCategory[] {
    return this.customTimeRangeCategories.filter((category) => !category.isActive)
  }

  get hasActiveCategories(): boolean {
    return this.activeCategories.length > 0
  }

  get hasTimeSettingsCategories(): boolean {
    return this.customTimeRangeCategories.length > 0
  }

  /**
   * Alias for models.map
   */
  map<P>(callback: (category: CustomTimeRangeCategory) => P): P[] {
    return this.customTimeRangeCategories.map(callback)
  }
}

export class CustomTimeRangeCategory {
  id: number
  companyId: number
  name: string
  timeType: TimeSettingsEnumsTimeTypeEnum
  archived: boolean
  default: boolean
  categoryType: TimeSettingsEnumsCategoryTypeEnum

  constructor(category: TimeSettingsCustomTimeRangeCategory) {
    this.id = category.id
    this.companyId = category.companyId
    this.name = category.name
    this.timeType = category.timeType
    this.archived = category.archived
    this.default = category.default
    this.categoryType = category.categoryType
  }

  get isRegular() {
    return this.timeType === 'regular'
  }

  get isOvertime() {
    return this.timeType === 'overtime'
  }

  get isAnyTime() {
    return this.timeType === 'any_time'
  }

  get isActive() {
    return !this.archived
  }
}
